/**
 * MediaPreview Component
 *
 * Displays a media item preview (image or video) with menu options.
 * Used in the upload media section to show uploaded content.
 */

import { GripHorizontal, Pencil, Trash2 } from "lucide-react"
import { useState } from "react"
import { ImagePlaceholderIcon, PlayIcon, VideoCameraIcon } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent } from "@/components/ui/dialog"

interface MediaPreviewProps {
  type: "image" | "video"
  src?: string
  title?: string
  url?: string
  isCover?: boolean
  isAiGenerated?: boolean
  variant?: "default" | "editProfile"
  onCoverSelect?: () => void
  onRemove?: () => void
  onChange?: () => void
  onRegenerate?: () => void
}

export function MediaPreview({
  type,
  src,
  isCover = false,
  isAiGenerated = false,
  variant = "default",
  onCoverSelect,
  onRemove,
  onChange,
  onRegenerate,
}: MediaPreviewProps) {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false)

  const handleOpenVideoModal = () => {
    setIsVideoModalOpen(true)
  }

  if (type === "video") {
    return (
      <>
        <Dialog open={isVideoModalOpen} onOpenChange={setIsVideoModalOpen}>
          <DialogContent className="max-w-3xl w-full p-0 bg-black/90 border-none">
            <video
              src={src}
              className="w-full h-auto max-h-[80vh] object-contain"
              controls
              autoPlay
            />
          </DialogContent>
        </Dialog>
        <div className="flex items-center gap-4 rounded-lgx relative group">
          {/* Video preview using HTML5 video element */}
          <div className="w-50 h-30 bg-themed rounded flex items-center justify-center flex-shrink-0 overflow-hidden relative">
            {src ? (
              <video src={src} className="w-full h-full object-cover" muted preload="metadata" />
            ) : (
              <VideoCameraIcon className="text-brown-primary" />
            )}
            {/* Play icon overlay when we have a valid source - opens modal */}
            {src && (
              <>
                <div
                  className="absolute inset-0 flex items-center justify-center z-5 cursor-pointer"
                  onClick={handleOpenVideoModal}
                >
                  <div className="w-8 h-8 bg-transparent border-2 border-white rounded-full flex items-center justify-center">
                    <PlayIcon className="text-brand-dark" />
                  </div>
                </div>
                <div className="absolute inset-0 bg-[rgb(0,0,0,0.5)] z-2" />
              </>
            )}
          </div>

          {/* Edit and Delete icons for video */}
          {src && (
            <div className="flex items-center gap-0">
              {onChange && (
                <button
                  type="button"
                  onClick={onChange}
                  className="p-2 rounded-full bg-transparent text-white transition-colors cursor-pointer"
                  aria-label="Edit video"
                >
                  <Pencil className="w-4 h-4" />
                </button>
              )}
              {onRemove && (
                <button
                  type="button"
                  onClick={onRemove}
                  className="p-2 rounded-full bg-transparent text-white transition-colors cursor-pointer"
                  aria-label="Delete video"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>
      </>
    )
  }

  // Image preview
  return (
    <div className="relative aspect-4/3 md:w-35 md:h-35 rounded-lg bg-themed overflow-hidden group">
      {/* Top row - visible on hover */}
      <div className="absolute top-0 left-1 right-0 p-1 pt-2 flex items-center justify-between z-50 opacity-0 group-hover:opacity-100 transition-opacity">
        {/* Left: Grip */}
        <GripHorizontal className="w-5 h-5 text-white cursor-pointer" />

        {/* Right: Edit + Remove icons */}
        <div className="flex items-center gap-0">
          <button type="button" className="p-1 rounded-full cursor-pointer">
            <Pencil className="!w-5 !h-5 text-white" />
          </button>
          {onRemove && (
            <button
              type="button"
              onClick={e => {
                e.stopPropagation()
                onRemove()
              }}
              className="p-1 rounded-full cursor-pointer"
            >
              <Trash2 className="!w-5 !h-5 text-white" />
            </button>
          )}
        </div>
      </div>

      {/* Center overlay - visible on hover */}
      <div className="absolute pb-2 inset-0 bg-[rgb(0,0,0,0.6)] opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-0 z-40 rounded-lg">
        {type === "image" && onCoverSelect && !isCover && variant !== "editProfile" && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={e => {
              e.stopPropagation()
              onCoverSelect?.()
            }}
            className="text-[rgb(0,0,0)] bg-white text-[13px] px-3 py-0"
          >
            Set as cover
          </Button>
        )}
        {isAiGenerated && onRegenerate && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={e => {
              e.stopPropagation()
              onRegenerate?.()
            }}
            className="text-[rgb(0,0,0)] bg-white"
          >
            Regenerate
          </Button>
        )}
      </div>

      {/* Image or placeholder */}
      {src ? (
        <div className="h-full w-full">
          <img src={src} alt="Uploaded" className="w-full h-full object-cover rounded-lg" />
        </div>
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <ImagePlaceholderIcon className="text-brown-primary" />
        </div>
      )}

      {/* Cover badge - shown when image is cover */}
      {isCover && (
        <div className="absolute bottom-0 left-0 right-0 px-3 py-1.5 bg-[rgb(0,0,0,0.7)] z-30">
          <p className="text-[13px] font-medium text-white text-center">Cover</p>
        </div>
      )}
    </div>
  )
}
